import React, { ElementType, FC, ReactNode } from 'react'
import { omit } from 'lodash'
import { TypographyProps, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { html2Text as html2TextStr } from './stringHelper'

export interface StyledTypographyProps extends TypographyProps {
  /**
   * String to display in this component.
   */
  str?: string | null
  /**
   * If `true`, variant={variant} || 'h1'.
   * @default: false, variant={variant} || 'body1'
   */
  h1?: boolean
  /**
   * If `true`, variant={variant} || 'h2'.
   * @default: false, variant={variant} || 'body1'
   */
  h2?: boolean
  /**
   * If `true`, variant={variant} || 'h3'.
   * @default: false, variant={variant} || 'body1'
   */
  h3?: boolean
  /**
   * If `true`, variant={variant} || 'h4'.
   * @default: false, variant={variant} || 'body1'
   */
  h4?: boolean
  /**
   * If `true`, variant={variant} || 'h5'.
   * @default: false, variant={variant} || 'body1'
   */
  h5?: boolean
  /**
   * If `true`, variant={variant} || 'h6'.
   * @default: false, variant={variant} || 'body1'
   */
  h6?: boolean
  /**
   * If `true`, variant={variant} || 'subtitle1'.
   * @default: false, variant={variant} || 'body1'
   */
  subtitle1?: boolean
  /**
   * If `true`, variant={variant} || 'subtitle2'.
   * @default: false, variant={variant} || 'body1'
   */
  subtitle2?: boolean
  /**
   * If `true`, variant={variant} || 'body1'.
   * @default: false, variant={variant} || 'body1'
   */
  body1?: boolean
  /**
   * If `true`, variant={variant} || 'body2'.
   * @default: false, variant={variant} || 'body1'
   */
  body2?: boolean
  /**
   * If `true`,
   *    textTransform=startCase ? 'capitalize' : overline || uppercase ? 'uppercase' : 'initial'
   *    lineHeight=overline ? theme.typography.overline.lineHeight : small ? '22px' : large ? '57px' : ''
   * @default: false
   */
  overline?: boolean
  /**
   * The component used for the root node.
   * Either a string to use a HTML element or a component.
   * @default: div
   */
  component?: ElementType
  /**
   * If `true`,
   *    lineHeight=overline ? theme.typography.overline.lineHeight : small ? '22px' : large ? '57px' : ''
   * @default: false
   */
  small?: boolean
  /**
   * If `true`,
   *    lineHeight=overline ? theme.typography.overline.lineHeight : small ? '22px' : large ? '57px' : ''
   * @default: false
   */
  large?: boolean
  /**
   * If `true`,
   *    textTransform=startCase ? 'capitalize' : overline || uppercase ? 'uppercase' : 'initial'
   * @default: false
   */
  uppercase?: boolean
  /**
   * The height of the Typography.
   * @default: auto
   */
  height?: string
  /**
   * The color of the Typography, if `true`
   *    color: error
   *           ? theme.palette.error.main
   *           : link
   *           ? '#3D8EDE'
   *           : grey
   *           ? '#555555'
   *           : secondary
   *           ? theme.palette.secondary.main
   *           : ''
   * @default: false
   */
  grey?: boolean
  /**
   * The color of the Typography, if `true`
   *    color: error
   *           ? theme.palette.error.main
   *           : link
   *           ? '#3D8EDE'
   *           : grey
   *           ? '#555555'
   *           : secondary
   *           ? theme.palette.secondary.main
   *           : ''
   * @default: false
   */
  error?: boolean
  /**
   * The font-weight of the Typography.
   */
  fontWeight?: string | number
  /**
   * If `true`,
   *    fontWeight: fontWeight ?? (bold ? 700 : semiBold ? 600 : regular ? 400 : thin ? 100 : 400)
   * @default: false
   */
  bold?: boolean
  /**
   * If `true`,
   *    fontWeight: fontWeight ?? (bold ? 700 : semiBold ? 600 : regular ? 400 : thin ? 100 : 400)
   * @default: false
   */
  semiBold?: boolean
  /**
   * If `true`,
   *    fontWeight: fontWeight ?? (bold ? 700 : semiBold ? 600 : regular ? 400 : thin ? 100 : 400)
   * @default: false
   */
  regular?: boolean
  /**
   * If `true`,
   *    fontWeight: fontWeight ?? (bold ? 700 : semiBold ? 600 : regular ? 400 : thin ? 100 : 400)
   * @default: false
   */
  thin?: boolean
  /**
   * The font-family of the Typography.
   */
  fontFamily?: string
  /**
   * The text-align of the Typography. If `true`, textAlign='center'
   * @default: false, textAlign='initial'
   */
  center?: boolean
  /**
   * Empty string indicator, eg: '-'.
   */
  emptyString?: string
  /**
   * The font-size of the Typography.
   */
  fontSize?: string | number
  /**
   * If `true`,
   *    color: error
   *           ? theme.palette.error.main
   *           : link
   *           ? '#3D8EDE'
   *           : grey
   *           ? '#555555'
   *           : secondary
   *           ? theme.palette.secondary.main
   *           : ''
   *    cursor: link || onClick ? 'pointer' : ''
   * @default: false
   */
  link?: boolean
  /**
   * If `true`, display: inline ? 'inline-block' : 'flex'.
   * @default: false
   */
  inline?: boolean
  /**
   * If `true`, will convert the string to html.
   */
  html2Text?: boolean
  /**
   * If `true`, textTransform='capitalize'.
   * @default: false
   */
  startCase?: boolean
  /**
   * If `true`,
   *    color: error
   *           ? theme.palette.error.main
   *           : link
   *           ? '#3D8EDE'
   *           : grey
   *           ? '#555555'
   *           : secondary
   *           ? theme.palette.secondary.main
   *           : ''
   * @default: false
   */
  secondary?: boolean
  /**
   * The content of the component.
   */
  children?: ReactNode
  onClick?: () => void
}

const variantArray = [
  'body1',
  'body2',
  'button',
  'caption',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'inherit',
  'overline',
  'subtitle1',
  'subtitle2'
]

/**
 * Styled Typography
 *
 * @link https://mui.com/material-ui/react-typography/
 */
const StyledTypography: FC<StyledTypographyProps> = props => {
  const {
    str,
    overline = false,
    variant,
    component = 'div',
    small = false,
    large = false,
    uppercase = false,
    sx = {},
    height = 'auto',
    grey = false,
    error = false,
    fontWeight,
    bold = false,
    semiBold = false,
    regular = false,
    thin = false,
    fontFamily,
    fontSize,
    center = false,
    emptyString = '',
    link = false,
    inline = false,
    html2Text = false,
    startCase = false,
    onClick,
    className = '',
    secondary = false,
    children,
    ...rest
  } = props
  const theme = useTheme()
  const computedVariant = (variant ||
    variantArray.filter(v => props[v as keyof StyledTypographyProps])[0] ||
    'body1') as StyledTypographyProps['variant']

  const handleStartCase = (str: string | ReactNode) => {
    if (startCase) {
      if (typeof str === 'string') {
        return str.toLowerCase()
      }
    }
    return str
  }
  const renderStr = () => {
    if (html2Text) {
      return handleStartCase(html2TextStr(str || (children as string) || ''))
    }
    return handleStartCase(str ?? children ?? emptyString)
  }
  return (
    <Typography
      {...omit(rest, variantArray)}
      component={component}
      variant={computedVariant}
      sx={{
        display: inline ? 'inline-block' : 'flex',
        fontFamily,
        fontWeight: fontWeight ?? (bold ? 700 : semiBold ? 600 : regular ? 400 : thin ? 100 : 400),
        fontSize,
        color: error
          ? theme.palette.error.main
          : link
          ? '#3D8EDE'
          : grey
          ? '#555555'
          : secondary
          ? theme.palette.secondary.main
          : '',
        height,
        textTransform: startCase ? 'capitalize' : overline || uppercase ? 'uppercase' : 'initial',
        lineHeight: overline ? theme.typography.overline.lineHeight : small ? '22px' : large ? '57px' : '',
        textAlign: center ? 'center' : 'initial',
        cursor: link || onClick ? 'pointer' : '',
        ...sx
      }}
      className={className}
      onClick={onClick}
    >
      {renderStr()}
    </Typography>
  )
}

export default StyledTypography
